import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { weeklyProgramsAPI } from '../../services/api'
import { WeeklyScoreBanner } from './WeeklyScoreBanner'

export function ReportModal({ program, onClose }) {
  const { t } = useTranslation()

  const { data: reportData, isLoading, isError } = useQuery({
    queryKey: ['programReport', program.id],
    queryFn: () => weeklyProgramsAPI.getReport(program.id, 80)
  })

  const report = reportData?.data

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-surface-container rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-surface-container">
          <h2 className="text-xl font-semibold text-gray-900">
            {t('programs.report.title')} - {t('programs.week')} {program.week_number}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="px-6 py-4">
          {isLoading && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">{t('common.loading')}</p>
            </div>
          )}

          {isError && (
            <div className="text-center py-8 text-red-600">
              {t('common.error')}
            </div>
          )}

          {report && (
            <div className="space-y-6">
              {program.status === 'submitted' && (
                <WeeklyScoreBanner programId={program.id} />
              )}

              {/* Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-lg mb-3">{t('programs.report.summary')}</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">{t('programs.report.sessionsCompleted')}</p>
                    <p className="text-2xl font-bold text-gray-900">{report.summary?.sessions_completed}/{report.summary?.sessions_planned}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('programs.report.completionRate')}</p>
                    <p className="text-2xl font-bold text-gray-900">{report.summary?.completion_rate}%</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('programs.report.totalVolume')}</p>
                    <p className="text-2xl font-bold text-gray-900">{Number(report.summary?.total_volume_kg || 0).toLocaleString()} kg</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('programs.report.trainingDuration')}</p>
                    <p className="text-2xl font-bold text-gray-900">{report.summary?.training_duration_minutes} min</p>
                  </div>
                </div>
              </div>

              {/* Metabolics */}
              <div className="bg-orange-50 rounded-lg p-4">
                <h3 className="font-semibold text-lg mb-3">{t('programs.report.metabolics')}</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">{t('programs.report.caloriesBurned')}</p>
                    <p className="text-2xl font-bold text-orange-600">{report.metabolics?.estimated_calories_burned} kcal</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('programs.report.avgPerSession')}</p>
                    <p className="text-2xl font-bold text-orange-600">{report.metabolics?.average_per_session} kcal</p>
                  </div>
                </div>
              </div>

              {/* Performance */}
              <div className="bg-green-50 rounded-lg p-4">
                <h3 className="font-semibold text-lg mb-3">{t('programs.report.performance')}</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">{t('programs.report.newPBs')}</p>
                    <p className="text-2xl font-bold text-green-600">{report.performance?.new_personal_bests}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('programs.report.exercisesImproved')}</p>
                    <p className="text-xl font-semibold text-green-600">{report.performance?.exercises_improved}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('programs.report.exercisesMaintained')}</p>
                    <p className="text-xl font-semibold text-gray-600">{report.performance?.exercises_maintained}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('programs.report.exercisesDeclined')}</p>
                    <p className="text-xl font-semibold text-red-600">{report.performance?.exercises_declined}</p>
                  </div>
                </div>

                {report.performance?.highlights?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">{t('programs.report.highlights')}</p>
                    <div className="space-y-2">
                      {report.performance.highlights.map((highlight, idx) => (
                        <div key={idx} className="bg-surface-container rounded p-2 text-sm">
                          <span className="font-medium">{highlight.exercise}</span>: {highlight.achievement}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Comparison */}
              {report.comparison_vs_last_week && (
                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="font-semibold text-lg mb-3">{t('programs.report.comparison')}</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">{t('programs.report.volumeChange')}</p>
                      <p className={`text-xl font-bold ${report.comparison_vs_last_week.volume_change.startsWith('+') ? 'text-green-600' : 'text-red-600'}`}>
                        {report.comparison_vs_last_week.volume_change}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">{t('programs.report.sessionsChange')}</p>
                      <p className={`text-xl font-bold ${report.comparison_vs_last_week.sessions_change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {report.comparison_vs_last_week.sessions_change >= 0 ? '+' : ''}{report.comparison_vs_last_week.sessions_change}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">{t('programs.report.trend')}</p>
                      <p className="text-xl font-bold text-blue-600">
                        {t(`programs.report.${report.comparison_vs_last_week.trend}`)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
